import { type ChangeEvent, useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";
import { AWSFeatureStatus } from "@doitintl/cmp-models";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Checkbox, Collapse, IconButton, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";

import { awsRealTimeTxt } from "../../../../../assets/texts/Settings/aws";
import mixpanel from "../../../../../utils/mixpanel";
import { AWSFeatureName, type Feature } from "../../types";
import { isBucketNameValid } from "../../utils";
import { FeaturesStatusChip } from "./FeaturesStatusChip";

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});
type FeaturesSelectionRowProps = {
  disabled: boolean;
  realTimeDataBucketName: string;
  feature: Feature;
  supportedFeatures: string[];
  partiallyEnabledFeatures: string[];
  handleFeatureSelection: (featureName: string, checked: boolean) => void;
  onRealTimeDataBucketNameChanged: (bucket: string) => void;
  smDown?: boolean;
};

export const FeaturesSelectionRow = ({
  feature,
  supportedFeatures,
  partiallyEnabledFeatures,
  disabled,
  handleFeatureSelection,
  onRealTimeDataBucketNameChanged,
  realTimeDataBucketName,
}: FeaturesSelectionRowProps) => {
  const classes = useRowStyles();
  const location = useLocation();

  const [rowExpanded, setRowExpanded] = useState(false);
  const [checked, setChecked] = useState(false);
  const [alreadySupported, setAlreadySupported] = useState<boolean>(false);
  const bucketNameIsValid = useMemo(() => isBucketNameValid(realTimeDataBucketName), [realTimeDataBucketName]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openRealTimeAnomaly = params.get("openRealTimeAnomaly") === "1";
    if (openRealTimeAnomaly && feature.name === "real-time-data") {
      setRowExpanded(true);
    }
  }, [location.search, feature.name]);

  useEffect(() => {
    setAlreadySupported(supportedFeatures.includes(feature.name));
  }, [supportedFeatures, feature.name]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    if (event.target.checked) {
      mixpanel.track("aws.cloud-connect.enable-real-time-data");
    }
    handleFeatureSelection(feature.name, event.target.checked);
  };

  const handleRealTimeDataBucketNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onRealTimeDataBucketNameChanged(event.target.value);
  };

  const detailedPermissions: string[] = useMemo(
    () => (feature.policies ? feature.policies : feature.permissions),
    [feature]
  );

  const featureStatus = useMemo((): AWSFeatureStatus | null => {
    if (
      feature.name === AWSFeatureName.realTimeData &&
      partiallyEnabledFeatures.includes(AWSFeatureName.realTimeData)
    ) {
      return AWSFeatureStatus.PartiallyEnabled;
    }

    if (alreadySupported) {
      return AWSFeatureStatus.Enabled;
    }

    return null;
  }, [feature.name, partiallyEnabledFeatures, alreadySupported]);

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => {
              setRowExpanded(!rowExpanded);
            }}
          >
            {rowExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {feature.displayName}
        </TableCell>
        <TableCell padding="none">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
            {featureStatus !== null && <FeaturesStatusChip status={featureStatus} />}
            {alreadySupported ? (
              <Tooltip title={awsRealTimeTxt.FEATURE_ALREADY_ENABLED}>
                <Box>
                  <Checkbox disabled={true} checked={true} />
                </Box>
              </Tooltip>
            ) : (
              <Checkbox
                color="primary"
                checked={checked}
                onChange={handleChange}
                disabled={disabled || feature.mandatory}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 1,
              }}
            >
              <Typography variant="subtitle1">{feature.description}</Typography>
              <Box
                sx={{
                  py: 1,
                }}
              >
                <Typography variant="subtitle2">Required {feature.policies ? "policies" : "permissions"}:</Typography>
              </Box>
              {detailedPermissions.map((permission, i) => (
                <div key={`t${i}`}>
                  <Typography component="div" variant="body2" color="textPrimary">
                    - {permission}
                  </Typography>
                </div>
              ))}
              {feature.textFields && (
                <Box sx={{ mt: 2, mb: 1.5 }}>
                  {feature.textFields.map((tf, i) => (
                    <div key={i}>
                      <Box
                        sx={{
                          mb: 1.5,
                        }}
                      >
                        {tf.title}
                      </Box>
                      <TextField
                        value={realTimeDataBucketName}
                        onChange={handleRealTimeDataBucketNameChange}
                        required={tf.required}
                        label={tf.label}
                        placeholder={tf.placeholder}
                        helperText={tf.helperText}
                        fullWidth
                        error={!bucketNameIsValid}
                      />
                    </div>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
